import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Toast } from "primereact/toast";
import ToastService from "../../services/ToastService";
import RegisterUserService from "../../services/RegisterUserService";
import AuthService from "../../services/AuthService";

function AddUser() {
  const { id } = useParams();
  const [Roles, setRoles] = useState([]);
  const ClientId = AuthService.getLoggedInClientId();

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    contactNumber: "",
    streetAddress: "",
    city: "",
    state: "",
    country: "",
    postalCode: "",
    panNumber: "",
    userImage: null,
    passportNumber: "",
    aadharNumber: "",
    roleId: "",
    clientId: ClientId,
  });

  const [formError, setFormError] = useState({});

  const navigate = useNavigate();
  const toast = useRef(null);
  const toastService = new ToastService(toast);

  useEffect(() => {
    if (id) {
      getUserById(id);
    }
    fetchRoles();
  }, [id]);

  const Role = {
    ADMIN: { roleId: 2, roleName: "Admin" },
    EMPLOYEE: { roleId: 4, roleName: "Employee" },
  };

  const REQUIRED_FIELDS = [
    "firstName",
    "lastName",
    "email",
    "contactNumber",
    "streetAddress",
    "city",
    "state",
    "country",
    "postalCode",
    "roleId",
  ];

  const isFieldEmpty = (value) => {
    if (value == null) return true;
    if (typeof value === "string") return value.trim() === "";
    if (typeof value === "number") return Number.isNaN(value);
    return false;
  };

  async function getUserById(userId) {
    try {
      const user = await RegisterUserService.getUserById(userId);
      setForm((prev) => ({
        ...prev,
        firstName: user.firstName ?? user.FirstName ?? "",
        lastName: user.lastName ?? user.LastName ?? "",
        email: user.email ?? user.Email ?? "",
        password: "",
        contactNumber: user.contactNumber ?? user.ContactNumber ?? "",
        streetAddress: user.streetAddress ?? user.StreetAddress ?? "",
        city: user.city ?? user.City ?? "",
        state: user.state ?? user.State ?? "",
        country: user.country ?? user.Country ?? "",
        postalCode: user.postalCode ?? user.PostalCode ?? "",
        panNumber: user.panNumber ?? user.PanNumber ?? "",
        passportNumber: user.passportNumber ?? user.PassportNumber ?? "",
        aadharNumber: user.aadharNumber ?? user.AadharNumber ?? "",
        roleId: String(user.roleId ?? user.RoleId ?? ""),
        userImage: user.userImage ?? user.UserImage ?? null,
        clientId: user.clientId ?? user.ClientId ?? ClientId,
      }));
    } catch (e) {
      console.log(e);
    }
  }

  const changeHandler = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    setFormError((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  async function fetchRoles() {
    try {
      const roles = await RegisterUserService.getAllRoles();
      setRoles(roles);
    } catch (e) {
      console.log(e);
    }
  }

  const onFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const userImage = reader.result.split(",")[1];
        setForm({ ...form, userImage });
        setFormError((prevErrors) => ({ ...prevErrors, userImage: "" }));
      };
      reader.onerror = (error) => {
        console.error("Error reading file:", error);
      };
    }
  };

  async function saveUser() {
    const userRoles = {
      roleId: form.roleId,
      userId: id || 0,
      isRowDeleted: false,
      createdBy: "1",
      modifiedBy: "1",
      createdOn: new Date(),
      modifiedOn: new Date(),
      isActive: true,
    };

    const requestData = {
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      password: form.password,
      contactNumber: form.contactNumber,
      streetAddress: form.streetAddress,
      city: form.city,
      state: form.state,
      country: form.country,
      postalCode: form.postalCode,
      roleId: form.roleId,
      panNumber: form.panNumber,
      userImage: form.userImage,
      passportNumber: form.passportNumber,
      aadharNumber: form.aadharNumber,
      userRoles: [userRoles],
      createdBy: 0,
      modifiedBy: 0,
      isActive: true,
      clientId: ClientId,
    };

    try {
      if (id) {
        await RegisterUserService.updateUser(id, requestData);
        toastService.showSuccess("Success", "User updated successfully");
      } else {
        await RegisterUserService.createUser(requestData);
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "User added successfully",
        });
      }
      resetForm();
      navigate("/registeruser");
    } catch (error) {
      const apiMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        (typeof error.response?.data === "string" ? error.response.data : null);

      if (apiMessage && /already exist/i.test(String(apiMessage))) {
        toastService.showError("Error", "User already exists.");
        setFormError((prev) => ({ ...prev, email: "User already exists." }));
        return;
      }

      if (error.response && error.response.status === 400) {
        const validationErrors = error.response.data?.errors;
        if (validationErrors) {
          setFormError(validationErrors);
        } else if (apiMessage) {
          toastService.showError("Error", apiMessage);
        }
      } else {
        toastService.showError("Error", "Could not save user.");
        console.log("Error saving user:", error);
      }
    }
  }

  const validateForm = () => {
    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const fieldsToValidate = id
      ? REQUIRED_FIELDS
      : [...REQUIRED_FIELDS, "password"];

    fieldsToValidate.forEach((key) => {
      if (isFieldEmpty(form[key])) {
        errors[key] = `Please enter ${key}`;
      }
    });

    if (
      form.email &&
      typeof form.email === "string" &&
      !emailRegex.test(form.email.trim())
    ) {
      errors.email = "Please enter a valid email address";
    }

    setFormError(errors);
    return Object.keys(errors).length === 0;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      saveUser();
    }
  };

  function resetForm() {
    setForm({
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      contactNumber: "",
      streetAddress: "",
      city: "",
      state: "",
      country: "",
      postalCode: "",
      panNumber: "",
      passportNumber: "",
      aadharNumber: "",
      roleId: "",
      clientId: "",
    });
    setFormError({});
  }

  const sectionStyle = {
    marginBottom: '28px',
  };
  const sectionHeadStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '18px',
    paddingBottom: '10px',
    borderBottom: '1.5px solid #f0f2f5',
  };
  const sectionIconStyle = (color) => ({
    width: '30px',
    height: '30px',
    borderRadius: '8px',
    background: color + '18',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: color,
    fontSize: '13px',
    flexShrink: 0,
  });
  const sectionTitleStyle = {
    fontSize: '13px',
    fontWeight: '700',
    color: '#344767',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    margin: 0,
  };
  const fieldGroupStyle = {
    marginBottom: '6px',
  };
  const labelStyle = {
    display: 'block',
    fontSize: '12px',
    fontWeight: '600',
    color: '#8392ab',
    marginBottom: '6px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  };
  const inputStyle = (hasError) => ({
    width: '100%',
    padding: '10px 14px',
    border: `1.5px solid ${hasError ? '#f5365c' : '#e9ecef'}`,
    borderRadius: '10px',
    fontSize: '14px',
    color: '#344767',
    background: '#fff',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
  });
  const errorStyle = {
    fontSize: '11px',
    color: '#f5365c',
    marginTop: '4px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  };

  return (
    <div style={{ padding: '24px', minHeight: '100vh', background: '#f4f6fb' }}>
      <Toast ref={toast} />
      <style>{`
        .au-input:focus { border-color: #344767 !important; box-shadow: 0 0 0 3px rgba(52,71,103,0.1) !important; }
        .au-select:focus { border-color: #344767 !important; box-shadow: 0 0 0 3px rgba(52,71,103,0.1) !important; outline: none; }
        .au-submit-btn:hover { opacity: 0.88; transform: translateY(-1px); }
      `}</style>

      {/* Page Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '28px' }}>
        <div style={{
          width: '48px', height: '48px', borderRadius: '12px',
          background: 'linear-gradient(135deg, #344767 0%, #21243d 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontSize: '20px', boxShadow: '0 4px 12px rgba(52,71,103,0.3)',
        }}>
          <i className="fas fa-user-plus" />
        </div>
        <div>
          <p style={{ fontSize: '22px', fontWeight: '700', color: '#344767', margin: 0, lineHeight: 1.2 }}>
            Add New Employee
          </p>
          <p style={{ fontSize: '13px', color: '#8392ab', margin: 0, marginTop: '2px' }}>
            Fill in the details below to register a new team member
          </p>
        </div>
      </div>

      <form onSubmit={onSubmit}>
        <div style={{
          background: '#fff', borderRadius: '20px',
          boxShadow: '0 4px 24px rgba(52,71,103,0.08)',
          overflow: 'hidden',
        }}>
          {/* Gradient header strip */}
          <div style={{
            background: 'linear-gradient(135deg, #344767 0%, #21243d 100%)',
            padding: '20px 28px',
            display: 'flex', alignItems: 'center', gap: '10px',
          }}>
            <i className="fas fa-id-badge" style={{ color: 'rgba(255,255,255,0.8)', fontSize: '16px' }} />
            <span style={{ color: '#fff', fontWeight: '600', fontSize: '15px' }}>Employee Information</span>
          </div>

          <div style={{ padding: '32px 28px' }}>

            {/* Section: Personal */}
            <div style={sectionStyle}>
              <div style={sectionHeadStyle}>
                <div style={sectionIconStyle('#5e72e4')}><i className="fas fa-user" /></div>
                <p style={sectionTitleStyle}>Personal Details</p>
              </div>
              <div className="row g-3">
                {[
                  { label: 'First Name', name: 'firstName', type: 'text', icon: 'fa-user' },
                  { label: 'Last Name', name: 'lastName', type: 'text', icon: 'fa-user' },
                  { label: 'Email Address', name: 'email', type: 'email', icon: 'fa-envelope' },
                ].map(({ label, name, type }) => (
                  <div className="col-md-4" key={name} style={fieldGroupStyle}>
                    <label style={labelStyle} htmlFor={name}>{label}</label>
                    <input
                      type={type}
                      id={name}
                      name={name}
                      className="au-input"
                      style={inputStyle(!!formError[name])}
                      value={form[name]}
                      onChange={changeHandler}
                      placeholder={label}
                    />
                    {formError[name] && <p style={errorStyle}><i className="fas fa-exclamation-circle" />{formError[name]}</p>}
                  </div>
                ))}
              </div>
            </div>

            {/* Section: Security & Contact */}
            <div style={sectionStyle}>
              <div style={sectionHeadStyle}>
                <div style={sectionIconStyle('#2dce89')}><i className="fas fa-shield-alt" /></div>
                <p style={sectionTitleStyle}>Security & Contact</p>
              </div>
              <div className="row g-3">
                <div className="col-md-4" style={fieldGroupStyle}>
                  <label style={labelStyle} htmlFor="password">Password</label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    className="au-input"
                    style={inputStyle(!!formError.password)}
                    value={form.password}
                    onChange={changeHandler}
                    placeholder="Password"
                  />
                  {formError.password && <p style={errorStyle}><i className="fas fa-exclamation-circle" />{formError.password}</p>}
                </div>
                <div className="col-md-4" style={fieldGroupStyle}>
                  <label style={labelStyle} htmlFor="contactNumber">Contact Number</label>
                  <input
                    type="text"
                    id="contactNumber"
                    name="contactNumber"
                    className="au-input"
                    style={inputStyle(!!formError.contactNumber)}
                    value={form.contactNumber}
                    maxLength="13"
                    onChange={(e) => { if (e.target.value.length <= 13) changeHandler(e); }}
                    placeholder="+91 XXXXX XXXXX"
                  />
                  {formError.contactNumber && <p style={errorStyle}><i className="fas fa-exclamation-circle" />{formError.contactNumber}</p>}
                </div>
                <div className="col-md-4" style={fieldGroupStyle}>
                  <label style={labelStyle} htmlFor="streetAddress">Street Address</label>
                  <input
                    type="text"
                    id="streetAddress"
                    name="streetAddress"
                    className="au-input"
                    style={inputStyle(!!formError.streetAddress)}
                    value={form.streetAddress}
                    onChange={changeHandler}
                    placeholder="Street Address"
                  />
                  {formError.streetAddress && <p style={errorStyle}><i className="fas fa-exclamation-circle" />{formError.streetAddress}</p>}
                </div>
              </div>
            </div>

            {/* Section: Location */}
            <div style={sectionStyle}>
              <div style={sectionHeadStyle}>
                <div style={sectionIconStyle('#fb6340')}><i className="fas fa-map-marker-alt" /></div>
                <p style={sectionTitleStyle}>Location</p>
              </div>
              <div className="row g-3">
                {[
                  { label: 'City', name: 'city', type: 'text' },
                  { label: 'State', name: 'state', type: 'text' },
                  { label: 'Country', name: 'country', type: 'text' },
                ].map(({ label, name, type }) => (
                  <div className="col-md-4" key={name} style={fieldGroupStyle}>
                    <label style={labelStyle} htmlFor={name}>{label}</label>
                    <input
                      type={type}
                      id={name}
                      name={name}
                      className="au-input"
                      style={inputStyle(!!formError[name])}
                      value={form[name]}
                      onChange={changeHandler}
                      placeholder={label}
                    />
                    {formError[name] && <p style={errorStyle}><i className="fas fa-exclamation-circle" />{formError[name]}</p>}
                  </div>
                ))}
                <div className="col-md-4" style={fieldGroupStyle}>
                  <label style={labelStyle} htmlFor="postalCode">Postal Code</label>
                  <input
                    type="number"
                    id="postalCode"
                    name="postalCode"
                    className="au-input"
                    style={inputStyle(!!formError.postalCode)}
                    value={form.postalCode}
                    onChange={changeHandler}
                    placeholder="Postal Code"
                  />
                  {formError.postalCode && <p style={errorStyle}><i className="fas fa-exclamation-circle" />{formError.postalCode}</p>}
                </div>
              </div>
            </div>

            {/* Section: Identity */}
            <div style={sectionStyle}>
              <div style={sectionHeadStyle}>
                <div style={sectionIconStyle('#11cdef')}><i className="fas fa-id-card" /></div>
                <p style={sectionTitleStyle}>Identity Documents</p>
              </div>
              <div className="row g-3">
                {[
                  { label: 'PAN Number', name: 'panNumber', type: 'text' },
                  { label: 'Passport Number', name: 'passportNumber', type: 'text' },
                  { label: 'Aadhar Number', name: 'aadharNumber', type: 'text' },
                ].map(({ label, name, type }) => (
                  <div className="col-md-4" key={name} style={fieldGroupStyle}>
                    <label style={labelStyle} htmlFor={name}>{label}</label>
                    <input
                      type={type}
                      id={name}
                      name={name}
                      className="au-input"
                      style={inputStyle(!!formError[name])}
                      value={form[name]}
                      onChange={changeHandler}
                      placeholder={label}
                    />
                    {formError[name] && <p style={errorStyle}><i className="fas fa-exclamation-circle" />{formError[name]}</p>}
                  </div>
                ))}
              </div>
            </div>

            {/* Section: Account */}
            <div style={{ ...sectionStyle, marginBottom: 0 }}>
              <div style={sectionHeadStyle}>
                <div style={sectionIconStyle('#344767')}><i className="fas fa-cog" /></div>
                <p style={sectionTitleStyle}>Account Settings</p>
              </div>
              <div className="row g-3">
                <div className="col-md-4" style={fieldGroupStyle}>
                  <label style={labelStyle} htmlFor="userImage">Profile Photo</label>
                  <input
                    type="file"
                    id="userImage"
                    name="userImage"
                    className="au-input form-control"
                    style={{ ...inputStyle(!!formError.userImage), padding: '7px 14px', cursor: 'pointer' }}
                    onChange={onFileChange}
                    accept="image/*"
                  />
                  {formError.userImage && <p style={errorStyle}><i className="fas fa-exclamation-circle" />{formError.userImage}</p>}
                </div>
                <div className="col-md-4" style={fieldGroupStyle}>
                  <label style={labelStyle} htmlFor="roleId">Role</label>
                  <select
                    id="roleId"
                    name="roleId"
                    className="au-select"
                    style={{ ...inputStyle(!!formError.roleId), appearance: 'none', cursor: 'pointer' }}
                    value={form.roleId}
                    onChange={changeHandler}
                  >
                    <option value="">Select Role</option>
                    <option value={Role.ADMIN.roleId}>{Role.ADMIN.roleName}</option>
                    <option value={Role.EMPLOYEE.roleId}>{Role.EMPLOYEE.roleName}</option>
                  </select>
                  {formError.roleId && <p style={errorStyle}><i className="fas fa-exclamation-circle" />{formError.roleId}</p>}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div style={{
            padding: '20px 28px',
            background: '#f8f9fe',
            borderTop: '1.5px solid #f0f2f5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '12px',
          }}>
            <button
              type="button"
              onClick={resetForm}
              style={{
                background: 'transparent',
                border: '1.5px solid #e9ecef',
                borderRadius: '10px',
                padding: '10px 24px',
                fontSize: '14px',
                fontWeight: '600',
                color: '#8392ab',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              <i className="fas fa-undo" style={{ marginRight: '7px' }} />
              Reset
            </button>
            <button
              type="submit"
              className="au-submit-btn"
              style={{
                background: 'linear-gradient(135deg, #344767 0%, #21243d 100%)',
                border: 'none',
                borderRadius: '10px',
                padding: '10px 32px',
                fontSize: '14px',
                fontWeight: '700',
                color: '#fff',
                cursor: 'pointer',
                boxShadow: '0 4px 15px rgba(52,71,103,0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s',
              }}
            >
              <i className="fas fa-user-check" />
              Create Employee
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default AddUser;
